import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, MessageSquare, TrendingUp, Calculator, FileText, Lightbulb } from "lucide-react";
import { Layout } from "@/components/layout";

export default function AIAssistantPage() {
  const [userMessage, setUserMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: "assistant",
      content: "Hello! I'm your Zentro AI Assistant. I can help you with bookkeeping questions, financial analysis, tax advice, and business insights. What would you like to know?"
    }
  ]);

  const quickActions = [
    {
      title: "Analyze Cash Flow",
      description: "Get insights on your cash flow patterns",
      icon: <TrendingUp className="h-5 w-5" />,
      prompt: "Analyze my business cash flow and provide insights"
    },
    {
      title: "Tax Advice",
      description: "Get help with tax planning and deductions",
      icon: <Calculator className="h-5 w-5" />,
      prompt: "What tax deductions should I consider for my business?"
    },
    {
      title: "Financial Reports",
      description: "Generate and explain financial reports",
      icon: <FileText className="h-5 w-5" />,
      prompt: "Help me understand my profit and loss statement"
    },
    {
      title: "Business Insights",
      description: "Get strategic business recommendations",
      icon: <Lightbulb className="h-5 w-5" />,
      prompt: "What are some ways to improve my business profitability?"
    }
  ];

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;

    const newMessage = {
      type: "user",
      content: userMessage
    };

    setMessages(prev => [...prev, newMessage]);
    setUserMessage("");
    setIsLoading(true);

    try {
      // Make API call to the backend AI endpoint
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          context: 'bookkeeping_assistant'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      
      const aiResponse = {
        type: "assistant",
        content: data.response || "I apologize, but I'm having trouble processing your request right now. Please try again."
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      const errorResponse = {
        type: "assistant",
        content: "I'm sorry, I'm experiencing technical difficulties. Please try again in a moment."
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (prompt: string) => {
    setUserMessage(prompt);
  };

  return (
    <Layout>
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-8 w-8 text-blue-500" />
            <h1 className="text-3xl font-bold">Zentro AI Assistant</h1>
            <Badge variant="secondary" className="ml-2">Beta</Badge>
          </div>
          <p className="text-muted-foreground">
            Your intelligent bookkeeping companion powered by advanced AI
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-6">
          {quickActions.map((action, index) => (
            <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleQuickAction(action.prompt)}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  {action.icon}
                  <CardTitle className="text-sm">{action.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-xs">{action.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Conversation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
              {messages.map((message, index) => (
                <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-lg ${
                    message.type === 'user' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <p className="text-sm">{message.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                    <p className="text-sm">AI is thinking...</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Textarea
                placeholder="Ask me anything about your business finances..."
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                className="min-h-[80px]"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button 
                onClick={handleSendMessage} 
                disabled={!userMessage.trim() || isLoading}
                size="icon"
                className="self-end mb-2"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>AI Capabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <h4 className="font-semibold">Financial Analysis</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Cash flow predictions</li>
                  <li>• Expense categorization</li>
                  <li>• Profitability analysis</li>
                  <li>• Budget recommendations</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Business Intelligence</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Tax optimization strategies</li>
                  <li>• Compliance monitoring</li>
                  <li>• Growth opportunities</li>
                  <li>• Risk assessment</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}